import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Brand from "@/components/lifted/Brand";
import { useAuth } from "@/context/AuthContext";
import { AUTH } from "@/constants/testIds";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // If we're already authenticated (returned to /auth/login by accident, e.g.
  // via back-button) jump straight to the dashboard instead of presenting an
  // empty form that looks like a "login reset".
  useEffect(() => {
    if (user && user !== false) {
      const to = location.state?.from?.pathname || "/dashboard";
      navigate(to, { replace: true });
    }
  }, [user, navigate, location.state]);

  const onSubmit = async (e) => {
    // Bulletproof against any browser quirk that could trigger a native form
    // GET submission (which on Safari would reload the page and put the
    // password in the URL bar).
    if (e) {
      try { e.preventDefault(); } catch (_) { /* noop */ }
      try { e.stopPropagation(); } catch (_) { /* noop */ }
    }
    setError(""); setBusy(true);
    try {
      const res = await login(email.trim(), password);
      if (res.ok) {
        navigate(location.state?.from?.pathname || "/dashboard", { replace: true });
      } else {
        setError(res.error || "Login failed. Please check your email and password.");
      }
    } catch (err) {
      setError(err?.message || "Login failed. Please try again.");
    } finally {
      setBusy(false);
    }
    return false;
  };

  return (
    <AuthScaffold>
      <form
        onSubmit={onSubmit}
        method="post"
        action="#"
        autoComplete="on"
        data-testid={AUTH.loginForm}
        className="space-y-5"
      >
        <div className="space-y-1">
          <div className="eyebrow">One login · One ecosystem</div>
          <h1 className="font-anton text-4xl sm:text-5xl uppercase leading-none">Sign in</h1>
          <p className="text-lifted-dim text-sm">Enter the unified Lifted command center.</p>
        </div>

        <div>
          <label className="lifted-label" htmlFor="email">Email</label>
          <input
            id="email" type="email" autoComplete="email" required
            className="input-lifted"
            value={email} onChange={(e) => setEmail(e.target.value)}
            data-testid={AUTH.loginEmail}
            placeholder="you@liftedatthemoment.com"
          />
        </div>

        <div>
          <label className="lifted-label" htmlFor="password">Password</label>
          <input
            id="password" type="password" autoComplete="current-password" required
            className="input-lifted"
            value={password} onChange={(e) => setPassword(e.target.value)}
            data-testid={AUTH.loginPassword}
            placeholder="••••••••"
          />
        </div>

        {error && (
          <div
            role="alert"
            data-testid={AUTH.loginError}
            className="flex items-start gap-3 rounded-lg border-2 border-rose-500/60 bg-rose-500/15 px-4 py-3"
          >
            <svg className="h-5 w-5 shrink-0 text-rose-300 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <circle cx="12" cy="16" r="0.5" fill="currentColor" />
            </svg>
            <div className="flex-1">
              <div className="font-bebas tracking-[0.18em] text-rose-200 text-sm">SIGN-IN FAILED</div>
              <div className="text-rose-100 text-sm mt-0.5">{error}</div>
            </div>
          </div>
        )}

        <button type="submit" disabled={busy} className="btn-lifted w-full" data-testid={AUTH.loginSubmit}>
          {busy ? "Entering…" : "Enter ecosystem"}
        </button>

        <div className="flex items-center justify-between pt-2 text-xs text-lifted-dim">
          <Link to="/auth/forgot-password" data-testid={AUTH.goForgot} className="hover:text-lifted-green tracking-widest font-bebas">
            Forgot password?
          </Link>
          <Link to="/auth/register" data-testid={AUTH.goRegister} className="hover:text-lifted-green tracking-widest font-bebas">
            Create account →
          </Link>
        </div>
      </form>
    </AuthScaffold>
  );
}

export function AuthScaffold({ children }) {
  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Visual side */}
      <div className="relative hidden lg:flex flex-col justify-between w-[46%] p-12 border-r border-white/5 overflow-hidden">
        <Brand />
        <div className="relative z-10 max-w-md">
          <div className="eyebrow mb-4">Lifted Ecosystem</div>
          <h2 className="headline-xl">
            One <span className="text-lifted-green">login</span>.<br />
            One <span className="text-lifted-purple">universe</span>.
          </h2>
          <p className="mt-6 text-lifted-dim text-base leading-relaxed max-w-sm">
            Shop, training, affiliates, drivers, partners, franchises — every Lifted role lives behind a single identity.
          </p>
          <div className="mt-8 flex flex-wrap gap-2">
            {["Shop", "Training", "Affiliate", "Delivery", "Partner", "Franchise", "Admin"].map((t) => (
              <span key={t} className="chip">{t}</span>
            ))}
          </div>
        </div>
        <div className="text-[0.65rem] tracking-[0.3em] text-lifted-mute font-bebas relative z-10">
          POWERED BY THE LIFTED COSMOS
        </div>
      </div>

      {/* Form side */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8"><Brand /></div>
          <div className="glass-panel p-8 sm:p-10 fade-up">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}
